import { useCallback, useEffect, useRef, useState } from 'react';
import { Alert, Button, Drawer, Flex, Space, Spin, Typography, message } from 'antd';
import { RobotOutlined, FullscreenOutlined, QuestionCircleOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { useAiAgent } from '@/contexts/AiAgentContext';
import { streamAiAgentChat, fetchAssistantSuggestions, postChatFeedback, type AiSseChunk } from '@/services/aiAgent.service';
import { AgentInput } from './AgentInput';
import { AgentMessages, type AgentMessage } from './AgentMessages';
import { ContextBadge } from './ContextBadge';
import { PersonaBadge } from './PersonaBadge';
import { ActionConfirmModal } from './ActionConfirmModal';
import type { ActionPayload } from './action-payload';
import { fetchAiAgentConfig } from '@/services/aiAgentConfig.service';

const { Text } = Typography;
const QUICK_PROMPTS_BY_MODULE: Record<string, string[]> = {
  sales: [
    'What is total sales this month?',
    'Show top 10 customers by revenue.',
    'Which customer orders are at risk?',
    'Show quotation pipeline value.',
    'Show delivered but not invoiced value.',
  ],
  finance: [
    'Show AR aging buckets.',
    'Show overdue customer invoices.',
    'Trace receivables by customer and project.',
    'Compare sales vs collections this month.',
    'Show supplier bills due this week.',
  ],
  procurement: [
    'What is open PO value?',
    'Which purchase orders are overdue?',
    'Show supplier delay leaderboard.',
    'Show pending GRNs.',
  ],
  manufacturing: [
    'Show production overview KPIs.',
    'Which work orders are delayed?',
    'Show workstation status summary.',
    'Show manufacturing inspection backlog.',
  ],
  inventory: [
    'Show stock value by warehouse.',
    'Which items are below reorder level?',
    'How many SKUs are low stock?',
    'Show slow moving inventory.',
  ],
  quality: [
    'Show open inspections summary.',
    'How many open NCRs?',
    'Show pending inspections list.',
    'Show rejection trend.',
  ],
  scm: [
    'Show delivery performance by customer.',
    'Show pending dispatch deliveries.',
    'Show deliveries delayed.',
  ],
  general: [
    'What should I worry about today?',
    'What predictive risks should I watch?',
    'Where is money stuck?',
    'Trace receivables by customer and project.',
    'Why is production delayed?',
    'Why are deliveries delayed?',
    'What are my pending tasks today?',
  ],
};

const HOW_TO_PROMPTS_BY_MODULE: Record<string, string[]> = {
  sales: ['How do I create a quotation?', 'What is the workflow from quotation to invoice?'],
  finance: ['How do I record a customer receipt?', 'How do I post a journal entry?'],
  procurement: ['How do I book a purchase order?', 'How do I receive goods against a PO?'],
  manufacturing: ['How do I create a work order?', 'How do I record production output?'],
  inventory: ['How do I transfer stock between warehouses?', 'How do I issue material?'],
  quality: ['How do I raise an NCR?', 'How do I record an inspection?'],
  scm: ['How do I create a delivery note?'],
  hr: ['How do I apply for leave?', 'How do I run payroll?'],
  general: ['How do I use this screen?', 'How do I book a purchase order?'],
};

function genId(): string {
  return `m-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;
}

function getOrCreateSessionId(): string {
  try {
    const k = 'ai_agent_session_id';
    const existing = sessionStorage.getItem(k);
    if (existing) return existing;
    const sid = typeof crypto !== 'undefined' && crypto.randomUUID ? crypto.randomUUID() : `sid-${Date.now()}`;
    sessionStorage.setItem(k, sid);
    return sid;
  } catch {
    return `sid-${Date.now()}`;
  }
}

function mergeQuickPrompts(personalized: string[], defaults: string[], max = 8): string[] {
  const seen = new Set<string>();
  const out: string[] = [];
  for (const prompt of [...personalized, ...defaults]) {
    const key = prompt.trim().toLowerCase();
    if (!key || seen.has(key)) continue;
    seen.add(key);
    out.push(prompt);
    if (out.length >= max) break;
  }
  return out;
}

/**
 * Global right-side AI panel. Toggle: **Ctrl+Shift+A** (or use `useAiAgent().setDrawerOpen`).
 */
export function AgentDrawer() {
  const navigate = useNavigate();
  const { drawerOpen, setDrawerOpen, pageContext, pendingPrompt, clearPendingPrompt } = useAiAgent();
  const [messages, setMessages] = useState<AgentMessage[]>([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [confirmPayload, setConfirmPayload] = useState<ActionPayload | null>(null);
  const sessionIdRef = useRef(getOrCreateSessionId());
  const lastUserMessageRef = useRef('');
  const lastToolNameRef = useRef<string | null>(null);
  const abortRef = useRef<AbortController | null>(null);
  const moduleKey = String(pageContext.module || '').toLowerCase();
  const defaultQuickPrompts = QUICK_PROMPTS_BY_MODULE[moduleKey] || QUICK_PROMPTS_BY_MODULE.general;
  const howToPrompts = HOW_TO_PROMPTS_BY_MODULE[moduleKey] || HOW_TO_PROMPTS_BY_MODULE.general;
  const [quickPrompts, setQuickPrompts] = useState(defaultQuickPrompts);
  const [assistantEnabled, setAssistantEnabled] = useState(true);

  useEffect(() => {
    setQuickPrompts(defaultQuickPrompts);
  }, [moduleKey]);

  useEffect(() => {
    if (!drawerOpen) return;
    let alive = true;
    void (async () => {
      try {
        const res = await fetchAiAgentConfig();
        if (alive) setAssistantEnabled(res.config?.enabled !== false);
      } catch {
        if (alive) setAssistantEnabled(true);
      }
    })();
    return () => {
      alive = false;
    };
  }, [drawerOpen]);

  useEffect(() => {
    if (!drawerOpen || !assistantEnabled) return;
    let alive = true;
    void (async () => {
      try {
        const res = await fetchAssistantSuggestions(moduleKey || 'general');
        if (!alive || !res.personalized?.length) return;
        const defaults = res.moduleDefaults?.length ? res.moduleDefaults : defaultQuickPrompts;
        setQuickPrompts(mergeQuickPrompts(res.personalized, defaults));
      } catch {
        // keep module defaults
      }
    })();
    return () => {
      alive = false;
    };
  }, [drawerOpen, assistantEnabled, moduleKey, defaultQuickPrompts]);

  const applyChunk = useCallback(
    (c: AiSseChunk, assistantId: string) => {
      if (c.type === 'text') {
        setMessages((prev) =>
          prev.map((x) =>
            x.id === assistantId && x.role === 'assistant' ? { ...x, text: x.text + c.content } : x
          )
        );
        return;
      }
      if (c.type === 'tool_start') {
        lastToolNameRef.current = c.toolName;
        return;
      }
      if (c.type === 'tool_result') {
        setMessages((prev) => [
          ...prev,
          { id: genId(), role: 'tool', toolName: lastToolNameRef.current || 'tool', data: c.data },
        ]);
        lastToolNameRef.current = null;
        return;
      }
      if (c.type === 'error') {
        setMessages((prev) => [...prev, { id: genId(), role: 'error', text: c.message }]);
        return;
      }
      if (c.type === 'grounding_memories' && c.items?.length) {
        setMessages((prev) =>
          prev.map((x) =>
            x.id === assistantId && x.role === 'assistant'
              ? { ...x, memoryCitations: c.items }
              : x
          )
        );
        return;
      }
      if (c.type === 'action_required') {
        setConfirmPayload((c.payload as ActionPayload) ?? null);
        setConfirmOpen(true);
      }
    },
    []
  );

  const runStream = useCallback(
    async (userText: string, confirmed: boolean) => {
      const assistantId = genId();
      setMessages((prev) => [...prev, { id: assistantId, role: 'assistant', text: '' }]);
      lastToolNameRef.current = null;
      abortRef.current = new AbortController();
      try {
        await streamAiAgentChat(
          {
            message: userText,
            sessionId: sessionIdRef.current,
            pageContext: { ...pageContext } as Record<string, unknown>,
            confirmed,
            enableSemanticSearch: true,
          },
          (chunk) => applyChunk(chunk, assistantId),
          { signal: abortRef.current.signal }
        );
      } catch (e) {
        const msg = e instanceof Error ? e.message : String(e);
        setMessages((prev) => [...prev, { id: genId(), role: 'error', text: msg }]);
      } finally {
        abortRef.current = null;
      }
    },
    [applyChunk, pageContext]
  );

  const submitMessage = useCallback(
    async (text: string, confirmed = false) => {
      const trimmed = text.trim();
      if (!trimmed || !assistantEnabled) return;
      lastUserMessageRef.current = trimmed;
      setMessages((prev) => [...prev, { id: genId(), role: 'user', text: trimmed }]);
      setLoading(true);
      try {
        await runStream(trimmed, confirmed);
      } finally {
        setLoading(false);
      }
    },
    [assistantEnabled, runStream]
  );

  useEffect(() => {
    if (!drawerOpen || !pendingPrompt || loading || !assistantEnabled) return;
    const prompt = pendingPrompt;
    clearPendingPrompt();
    void submitMessage(prompt, false);
  }, [drawerOpen, pendingPrompt, loading, assistantEnabled, clearPendingPrompt, submitMessage]);

  const send = useCallback(async () => {
    const text = input.trim();
    if (!text || loading || !assistantEnabled) return;
    setInput('');
    await submitMessage(text, false);
  }, [input, loading, assistantEnabled, submitMessage]);

  const handleActionPrompt = useCallback(
    (prompt: string) => {
      if (loading) return;
      void submitMessage(prompt, false);
    },
    [loading, submitMessage]
  );

  const handleConfirm = useCallback(async () => {
    setConfirmOpen(false);
    if (!lastUserMessageRef.current) return;
    setLoading(true);
    try {
      await runStream(lastUserMessageRef.current, true);
    } finally {
      setLoading(false);
      setConfirmPayload(null);
    }
  }, [runStream]);

  const handleFeedback = useCallback(
    async (input: { messageId: string; rating: 'helpful' | 'not_helpful'; assistantText: string }) => {
      try {
        const res = await postChatFeedback({
          rating: input.rating,
          userMessage: lastUserMessageRef.current,
          assistantText: input.assistantText,
          sessionId: sessionIdRef.current,
          domain: pageContext.module,
        });
        if (res.candidateCreated) {
          message.info('Thanks — a memory candidate was created for review.');
        } else {
          message.success('Feedback recorded');
        }
      } catch (e) {
        message.error(e instanceof Error ? e.message : 'Feedback failed');
      }
    },
    [pageContext.module]
  );

  return (
    <>
      {!drawerOpen && (
        <div className="no-print" style={{ position: 'fixed', bottom: 24, right: 24, zIndex: 1000 }}>
          <Button
            type="primary"
            shape="circle"
            size="large"
            icon={<RobotOutlined />}
            onClick={() => setDrawerOpen(true)}
            title="Open AI assistant (Ctrl+Shift+A)"
            aria-label="Open AI assistant"
          />
        </div>
      )}
      <Drawer
        title={
          <Flex align="center" gap={8} wrap justify="space-between" style={{ width: '100%' }}>
            <Flex align="center" gap={8}>
              <RobotOutlined />
              <span>ERP Assistant</span>
            </Flex>
            <Button
              type="link"
              size="small"
              icon={<FullscreenOutlined />}
              onClick={() => {
                setDrawerOpen(false);
                navigate('/ai/workspace');
              }}
            >
              Open Full Workspace
            </Button>
          </Flex>
        }
        placement="right"
        width={Math.min(480, typeof window !== 'undefined' ? window.innerWidth - 16 : 480)}
        onClose={() => setDrawerOpen(false)}
        open={drawerOpen}
        destroyOnClose={false}
        styles={{ body: { paddingTop: 12, display: 'flex', flexDirection: 'column', height: '100%' } }}
      >
        <Flex vertical gap="small" style={{ flex: 1, minHeight: 0 }}>
          <div>
            <ContextBadge pageContext={pageContext} />
            <div style={{ marginTop: 8 }}>
              <PersonaBadge module={pageContext.module} />
            </div>
            <Space wrap size={[6, 6]} style={{ marginTop: 8 }}>
              {quickPrompts.map((prompt) => (
                <Button
                  key={prompt}
                  size="small"
                  onClick={() => setInput(prompt)}
                  disabled={loading}
                >
                  {prompt}
                </Button>
              ))}
            </Space>
            <Space wrap size={[6, 6]} style={{ marginTop: 6 }}>
              <Text type="secondary" style={{ fontSize: 12 }}>
                How-to:
              </Text>
              {howToPrompts.map((prompt) => (
                <Button
                  key={prompt}
                  size="small"
                  type="dashed"
                  icon={<QuestionCircleOutlined />}
                  onClick={() => handleActionPrompt(prompt)}
                  disabled={loading || !assistantEnabled}
                >
                  {prompt}
                </Button>
              ))}
            </Space>
            <Text type="secondary" style={{ fontSize: 11, display: 'block', marginTop: 6 }}>
              Shortcut: Ctrl+Shift+A
            </Text>
          </div>
          <div style={{ flex: 1, overflow: 'auto', minHeight: 200, padding: '4px 0' }}>
            {!assistantEnabled ? (
              <Alert
                type="warning"
                showIcon
                style={{ marginBottom: 8 }}
                message="Assistant is disabled"
                description="Your administrator has disabled ERP Assistant for this company."
              />
            ) : null}
            <Spin spinning={loading}>
              <AgentMessages
                messages={messages}
                onActionPrompt={handleActionPrompt}
                onFeedback={handleFeedback}
                onSaveUdq={(input) => {
                  const params = new URLSearchParams();
                  params.set('intent', input.intent);
                  params.set('new', '1');
                  if (input.title) params.set('q', input.title);
                  if (input.filters) params.set('filters', JSON.stringify(input.filters));
                  setDrawerOpen(false);
                  navigate(`/ai/udq?${params.toString()}`);
                }}
              />
            </Spin>
          </div>
          <AgentInput value={input} onChange={setInput} onSend={send} disabled={loading || !assistantEnabled} />
        </Flex>
      </Drawer>
      <ActionConfirmModal
        open={confirmOpen}
        payload={confirmPayload}
        onConfirm={handleConfirm}
        onCancel={() => {
          setConfirmOpen(false);
          setConfirmPayload(null);
        }}
      />
    </>
  );
}
